import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { events } from "@/mocks/events";
import StatusBadge from "@/components/base/StatusBadge";
import { formatDateShort, getWeekday } from "@/utils/format";
import usePageMeta from "@/hooks/usePageMeta";

const TODAY = "2026-10-15";

export default function Schedule() {
  const dates = useMemo(() => {
    const set: string[] = [];
    events.forEach((e) => {
      if (!set.includes(e.date)) set.push(e.date);
    });
    return set.sort();
  }, []);

  usePageMeta(
    "赛程安排 - 51每日大赛",
    "51每日大赛赛程安排页面，按日期查看未来与历史比赛时间表，快速定位当天全部赛事与开赛时间。",
    "赛程安排,比赛时间,赛事日历,今日赛程,51每日大赛",
  );
  const [selected, setSelected] = useState<string>(dates.includes(TODAY) ? TODAY : dates[0] ?? TODAY);
  const listTopRef = useRef<HTMLDivElement | null>(null);

  const dayEvents = events
    .filter((e) => e.date === selected)
    .sort((a, b) => (a.time < b.time ? -1 : 1));

  const jumpToToday = () => {
    setSelected(TODAY);
    listTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="w-full">
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">赛程安排</span>
          </nav>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">赛程安排</h1>
              <p className="mt-2 text-sm text-foreground-600 max-w-3xl leading-relaxed">
                按日期查看未来与历史比赛的时间表，选择日期即可快速定位当天的全部赛事。
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={jumpToToday}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-alarm-line" /> 跳转今日赛程
              </button>
              <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-background-300 bg-background-50 cursor-pointer">
                <i className="ri-calendar-line text-foreground-500" />
                <input
                  type="date"
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  className="bg-transparent text-sm text-foreground-800 focus:outline-none cursor-pointer"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8" ref={listTopRef}>
        <div className="flex items-center gap-3 mb-5 overflow-x-auto hide-scrollbar pb-1">
          <span className="shrink-0 text-xs text-foreground-500 mr-1">选择日期</span>
          {dates.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setSelected(d)}
              className={`shrink-0 flex flex-col items-center px-4 py-2 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${
                selected === d
                  ? "bg-primary-500 border-primary-500 text-background-50"
                  : "bg-background-50 border-background-200 text-foreground-700 hover:border-primary-300"
              }`}
            >
              <span className="font-heading text-sm">{formatDateShort(d)}</span>
              <span className={`text-[11px] ${selected === d ? "text-background-100" : "text-foreground-500"}`}>
                {getWeekday(d)}{d === TODAY ? " · 今日" : ""}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-4">
          <span className="w-1.5 h-4 rounded-full bg-primary-500" />
          <h2 className="font-heading text-lg text-foreground-950">
            {formatDateShort(selected)} {getWeekday(selected)} 赛程
          </h2>
          <span className="text-xs text-foreground-500">共 {dayEvents.length} 场</span>
        </div>

        {dayEvents.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-calendar-close-line text-4xl text-foreground-300" />
            <p className="mt-3 text-sm text-foreground-600">该日期暂无赛事安排，请选择其他日期</p>
            <button
              type="button"
              onClick={jumpToToday}
              className="mt-4 h-9 px-5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看今日赛程
            </button>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-[78px] top-2 bottom-2 w-px bg-background-200 hidden sm:block" />
            <div className="space-y-3">
              {dayEvents.map((e) => (
                <div key={e.id} className="relative flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                  <div className="hidden sm:flex items-center gap-3 w-[78px] shrink-0">
                    <span className="font-heading text-base text-foreground-950">{e.time}</span>
                    <span className="w-2.5 h-2.5 rounded-full bg-primary-500 -mr-[6px] relative z-10" />
                  </div>
                  <Link
                    to={`/event/${e.id}`}
                    className="flex-1 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 hover:bg-primary-50/40 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-label">{e.categoryName}</span>
                          <span className="text-[11px] text-foreground-500 sm:hidden">{e.time}</span>
                          <StatusBadge status={e.status} />
                        </div>
                        <h3 className="text-sm md:text-base font-medium text-foreground-950 truncate">{e.title}</h3>
                        <div className="mt-1.5 flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                          <span className="flex items-center gap-1"><i className="ri-map-pin-line" /> {e.venue}</span>
                          <span className="flex items-center gap-1"><i className="ri-trophy-line" /> {e.level}</span>
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line text-xl text-foreground-400" />
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}